import { useCallback, useEffect, useRef } from 'react';
import { useProxyStore } from '../store/proxy-store';
import { useProjectStore } from '../store/projectStore';
import { saveInterceptRulesForApplication } from '../utils/intercept-rules-storage';

const PERSIST_DEBOUNCE_MS = 300;

export function useInterceptRulesPersistence({
  onPersistError,
}: {
  onPersistError?: (params: { message: string }) => void;
} = {}) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedApplicationId = useProjectStore(
    (state) => state.selectedTarget?.id,
  );

  const cancelPendingPersist = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    cancelPendingPersist();

    return () => {
      cancelPendingPersist();
    };
  }, [selectedApplicationId, cancelPendingPersist]);

  const schedulePersistRulesToLocal = useCallback(() => {
    const applicationId = useProjectStore.getState().selectedTarget?.id;

    if (!applicationId || applicationId === 'standalone') {
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const currentApplicationId =
        useProjectStore.getState().selectedTarget?.id;

      if (!currentApplicationId || currentApplicationId === 'standalone') {
        return;
      }

      if (currentApplicationId !== applicationId) {
        return;
      }

      try {
        saveInterceptRulesForApplication({
          applicationId: currentApplicationId,
          rules: useProxyStore.getState().settings.interceptRules ?? [],
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to save intercept rules';

        onPersistError?.({ message });
        console.warn('Failed to persist intercept rules to local storage', {
          error: error instanceof Error ? error : new Error(String(error)),
          props: { applicationId: currentApplicationId },
        });
      }
    }, PERSIST_DEBOUNCE_MS);
  }, [onPersistError]);

  return {
    schedulePersistRulesToServer: schedulePersistRulesToLocal,
  };
}
